import React, { useEffect, useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
  LineChart,
  Line,
} from 'recharts';
import styles from '../styles/CreatorAnalytics.module.scss';
import api from '../../../utils/api';
import { CreatorDashboard } from '../types/creator.types';
import Spinner from '../../../components/Spinner';
import AnalyticsStatCard  from '../components/AnalyticsStartCard';
import { IoIosTrendingUp } from "react-icons/io";
import DateRangePicker from '../../../components/DateRange/DateRange';
import PrimaryButton from '../../../components/Button/PrimaryButton';
import { DateRange } from 'react-day-picker';

// ── Data ─────────────────────────────────────────────────────────────
const salesData = [
  { month: 'Week 1', sales: 185, orders: 42 },
  { month: 'Week 2', sales: 210, orders: 55 },
  { month: 'Week 3', sales: 195, orders: 48 },
  { month: 'Week 4', sales: 230, orders: 63 },
  { month: 'Week 5', sales: 218, orders: 59 },
];

const categoryData = [
  { name: 'Tops & Shirts', value: 231, pct: 28 },
  { name: 'Dresses', value: 215, pct: 26 },
  { name: "Children's Wear", value: 155, pct: 19 },
  { name: 'Bags & Accessories', value: 123, pct: 15 },
  { name: 'Outerwear', value: 103, pct: 12 },
];

const PIE_COLORS = ['#b5a642', '#4c3575', '#ef4444', '#7c3aed', '#10b981'];

const CATEGORY_FILTERS = ['All', 'Dresses', 'Tops & Shirts', 'Bags & Accessories', "Children's Wear", 'Outerwear'];
const FOR_WHO_FILTERS = ['All', 'Women', 'Men', 'Children', 'Baby'];
const SIZE_FILTERS = ['All', 'XS', 'S', 'M', 'L', 'XL'];
const CHART_TYPES = ['Pie', 'Bar', 'Line'] as const;
type ChartType = typeof CHART_TYPES[number];

// ── Icons ─────────────────────────────────────────────────────────────
const CartIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
  </svg>
);
const DollarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
  </svg>
);
const UsersIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
const BoxIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
);

// ── Sub-components ────────────────────────────────────────────────────


interface FilterPillProps {
  label: string;
  active: boolean;
  onClick: () => void;
  color?: string;
}
const FilterPill: React.FC<FilterPillProps> = ({ label, active, onClick, color }) => (
  <button
    className={`${styles.pill} ${active ? styles.pillActive : ''}`}
    style={active && color ? { background: color, borderColor: color } : undefined}
    onClick={onClick}
  >
    {label}
  </button>
);

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className={styles.tooltip}>
        <p className={styles.tooltipLabel}>{label}</p>
        {payload.map((p: any, i: number) => (
          <p key={i} style={{ color: p.color }}>{p.name}: <strong>{p.value}</strong></p>
        ))}
      </div>
    );
  }
  return null;
};

// ── Main Page ─────────────────────────────────────────────────────────
const CreatorAnalytics: React.FC = () => {
  const [activeCategories, setActiveCategories] = useState<string[]>(['All']);
  const [activeForWho, setActiveForWho] = useState<string[]>(['All']);
  const [activeSizes, setActiveSizes] = useState<string[]>(['All']);
  const [chartType, setChartType] = useState<ChartType>('Pie');
 const [analyticsData, setAnalyticsData] = useState<CreatorDashboard | null>(null);
 const [loading, setLoading] = useState(true);
 const [isYearMenuOpen, setIsYearMenuOpen] = useState<boolean>(false);
  const [range, setRange] = useState<DateRange | undefined>(undefined);
  const toggleFilter = (
    value: string,
    active: string[],
    setActive: React.Dispatch<React.SetStateAction<string[]>>,
    allItems: string[]
  ) => {
    if (value === 'All') {
      setActive(['All']);
      return;
    }
    const next = active.filter((v) => v !== 'All');
    if (next.includes(value)) {
      const result = next.filter((v) => v !== value);
      setActive(result.length === 0 ? ['All'] : result);
    } else {
      setActive([...next, value]);
    }
  };

  useEffect(() => {
  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      const response = await Promise.all([
        api.get('/creators/stats'),
        api.get('/creators/patterns/best-selling'),
      ]);
      const [dashboardStats, bestSellingPatterns] = await Promise.all(response.map((res) => res.data));
      setAnalyticsData({ ...dashboardStats, bestSellingPatterns });
    } catch (error) {
      console.error('Error fetching creator stats:', error);
    } finally {
      setLoading(false);
    }
  };
  fetchAnalyticsData();
  },[])

  const renderCategoryChart = () => {
    if (chartType === 'Pie') {
      return (
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie
              data={categoryData}
              cx="50%"
              cy="50%"
              outerRadius={100}
              dataKey="value"
              label={(props) => {
                const entry = categoryData[props.index as number];
                return `${props.name}: ${entry?.pct ?? 0}%`;
              }}
              labelLine
            >
              {categoryData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
            </Pie>
            <Tooltip formatter={(val) => [val, '']} />
          </PieChart>
        </ResponsiveContainer>
      );
    }
    if (chartType === 'Bar') {
      return (
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={categoryData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0ede8" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {categoryData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
    }
    return (
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={categoryData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0ede8" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip content={<CustomTooltip />} />
          <Line type="monotone" dataKey="value" stroke="#4c3575" strokeWidth={2.5} dot={{ fill: '#4c3575', r: 4 }} />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  if(loading) {
    return <Spinner/>
  }
  const handleApply = async () => {
    setLoading(true);
    const response = await Promise.all([
        api.get('/creators/stats', { params: { startDate: range?.from?.toISOString(), endDate: range?.to?.toISOString() } }),
        api.get('/creators/patterns/best-selling', { params: { startDate: range?.from?.toISOString(), endDate: range?.to?.toISOString() } }),
      ]);
      const [dashboardStats, bestSellingPatterns] = await Promise.all(response.map((res) => res.data));
      setAnalyticsData({ ...dashboardStats, bestSellingPatterns });
    setIsYearMenuOpen(false);
    setLoading(false);
  }

  

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Analytics &amp; Statistics</h1>
          <p className={styles.pageSubtitle}>Data from Jan 10, 2026 to Feb 9, 2026</p>
        </div>
      <div className={styles.dateRangeWrapper}>
                <PrimaryButton

                  onClick={() => setIsYearMenuOpen(!isYearMenuOpen)}>
                  {range?.from && range?.to
                    ? `${range.from.toDateString()} → ${range.to.toDateString()}`
                    : "Select date Range"}
                </PrimaryButton>


              {/* Popup calendar */}
              {isYearMenuOpen && (
                <div
                  className={styles.popupCalender}
                >
                  <DateRangePicker
                    range={range}
                    setRange={setRange}
                  />

                  <div className={styles.calenderFooter}>
                    <button onClick={() => setIsYearMenuOpen(false)} className={styles.calenderButton}>Cancel</button>
                    <button onClick={handleApply} className={styles.calenderButton}>Apply</button>
                  </div>
                </div>

              )}
              </div>
      </div>

      {/* Stat Cards */}
      <div className={styles.statsGrid}>
        <AnalyticsStatCard label="Total Orders" value={analyticsData?.stats.totalOrders.toString() || ''} changePct="+20.0%" change="up" iconClass="iconPrimary" icon={<CartIcon />} />
        <AnalyticsStatCard label="Average Order Value" value={analyticsData?.stats.averageOrderValue.toFixed(2) || '$0.00'} changePct="+5.2%" change="up" iconClass="iconAmber" icon={<DollarIcon />} />
        <AnalyticsStatCard label="Total Customers" value={analyticsData?.stats.totalCustomers.toString() || '0'} changePct="+14.3%" change="up" iconClass="iconNeutral" icon={<UsersIcon />} />
        {/* <AnalyticsStatCard   label="Pattern Downloads" value="540" changePct="+20.0%" change="up" iconClass="iconLight" icon={<BoxIcon />} /> */}
      </div>

      {/* Sales Performance Chart */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>Sales Performance</h2>
          <p className={styles.cardSubtitle}>Revenue and order trends for selected period</p>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={salesData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#4c3575" stopOpacity={0.85} />
                <stop offset="95%" stopColor="#4c3575" stopOpacity={0.2} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0ede8" />
            <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip />} />
            <Legend iconType="square" wrapperStyle={{ fontSize: 13, paddingTop: 12 }} />
            <Area type="monotone" dataKey="sales" name="Total Sales" stroke="#4c3575" strokeWidth={2} fill="url(#salesGrad)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Best Sellers + Category Distribution */}
      <div className={styles.twoCol}>
        {/* Best Sellers */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h2 className={styles.cardTitle}>Best Selling Patterns</h2>
            <p className={styles.cardSubtitle}>Top performers in selected period</p>
          </div>
          <div className={styles.sellerList}>
            {analyticsData?.bestSellingPatterns.map((item) => {
              const maxRevenue = Math.max(...analyticsData.bestSellingPatterns.map((b) => b.revenue));
              const barWidth = (item.revenue / maxRevenue) * 100;
              return (
                <div key={item.rank} className={styles.sellerRow}>
                  <span className={styles.sellerRank}>#{item.rank}</span>
                  <div className={styles.sellerInfo}>
                    <span className={styles.sellerName}>{item.name}</span>
                    <span className={styles.sellerSales}>{item.totalSold} sales</span>
                    <div className={styles.sellerBar}>
                      <div className={styles.sellerBarFill} style={{ width: `${barWidth}%`, background: item.color }} />
                    </div>
                  </div>
                  <span className={styles.sellerRevenue}>${item.revenue.toLocaleString()}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Category Distribution */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h2 className={styles.cardTitle}>Category Distribution</h2>
            <p className={styles.cardSubtitle}>Multi-select filters for detailed analysis</p>
          </div>

          <div className={styles.filterGroup}>
            <p className={styles.filterLabel}>Categories (Multi-select)</p>
            <div className={styles.pillRow}>
              {CATEGORY_FILTERS.map((f) => (
                <FilterPill
                  key={f} label={f}
                  active={activeCategories.includes(f)}
                  onClick={() => toggleFilter(f, activeCategories, setActiveCategories, CATEGORY_FILTERS)}
                  color="#4c3575"
                />
              ))}
            </div>
          </div>

          <div className={styles.filterGroup}>
            <p className={styles.filterLabel}>For Who (Multi-select)</p>
            <div className={styles.pillRow}>
              {FOR_WHO_FILTERS.map((f) => (
                <FilterPill
                  key={f} label={f}
                  active={activeForWho.includes(f)}
                  onClick={() => toggleFilter(f, activeForWho, setActiveForWho, FOR_WHO_FILTERS)}
                  color="#4c3575"
                />
              ))}
            </div>
          </div>

          <div className={styles.filterGroup}>
            <p className={styles.filterLabel}>Sizes (Multi-select)</p>
            <div className={styles.pillRow}>
              {SIZE_FILTERS.map((f) => (
                <FilterPill
                  key={f} label={f}
                  active={activeSizes.includes(f)}
                  onClick={() => toggleFilter(f, activeSizes, setActiveSizes, SIZE_FILTERS)}
                  color="#b45309"
                />
              ))}
            </div>
          </div>

          <div className={styles.filterGroup}>
            <p className={styles.filterLabel}>Chart Type</p>
            <div className={styles.pillRow}>
              {CHART_TYPES.map((t) => (
                <FilterPill
                  key={t} label={t}
                  active={chartType === t}
                  onClick={() => setChartType(t)}
                  color="#4c3575"
                />
              ))}
            </div>
          </div>

          <div className={styles.chartArea}>{renderCategoryChart()}</div>
        </div>
      </div>

      {/* Bottom Metric Cards */}
      <div className={styles.statsGrid}>
        <div className={styles.metricCard}>
          <div className={`${styles.metricIcon} ${styles.metricIconGreen}`}><IoIosTrendingUp size={20} /></div>
          <div>
            <p className={styles.metricLabel}>Revenue Growth</p>
            <p className={styles.metricValue}>+24.3%</p>
            <p className={styles.metricSub}>vs previous period</p>
          </div>
        </div>
        <div className={styles.metricCard}>
          <div className={`${styles.metricIcon} ${styles.metricIconBlue}`}><CartIcon /></div>
          <div>
            <p className={styles.metricLabel}>Conversion Rate</p>
            <p className={styles.metricValue}>46.88%</p>
            <p className={styles.metricSub}>for selected period</p>
          </div>
        </div>
        <div className={styles.metricCard}>
          <div className={`${styles.metricIcon} ${styles.metricIconPurple}`}><BoxIcon /></div>
          <div>
            <p className={styles.metricLabel}>Avg. Patterns/Order</p>
            <p className={styles.metricValue}>1.5</p>
            <p className={styles.metricSub}>for selected period</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatorAnalytics;